import { FormikProps } from 'formik';
import { useMemo } from 'react';
import useCustomers from '../../../../../hooks/useCustomers';
import useDeclarations from '../../../../../hooks/useDeclarations';
import useIndividuals from '../../../../../hooks/useIndividuals';
import useSession from '../../../../../hooks/useSession';
import InvoiceModalButton from '../../../ireland/import/h1/invoice/InvoiceModalButton';
import AlertManager from './alerts/AlertManager';
import SubmissionModalsManager from './SubmissionModalsManager';
import SubmissionErrorsDrawerButton from 'views/declarations/common/submissionErrors/SubmissionErrorsDrawerButton';
import DeclarationNotificationsDrawerButton from 'views/declarations/common/declaration-errors-drawer/DeclarationNotificationsDrawerButton';
import shouldShowSubmissionErrors from './shouldShowSubmissionErrors';

interface Props {
    submitting: boolean;
    submissionModalsActions: { validateForm: () => Promise<boolean>; onCancel: () => void };
    formik: FormikProps<any>;
    saveAsDraft: Function;
}

const DeclarationPopupManager = ({ submitting, submissionModalsActions, formik, saveAsDraft }: Props) => {
    const { declaration, error } = useDeclarations();
    const { userInfo } = useSession();
    const { individual } = useIndividuals({ individualId: userInfo?.individualId });
    const { customer } = useCustomers({ customerId: userInfo?.customerId });

    const hasRightCertificate: boolean | undefined = useMemo(() => {
        if (declaration?.cdsDeclaration || declaration?.gvmsDeclaration || declaration?.ukNctsDeclaration) {
            return individual?.hasHmrcToken;
        } else if (
            declaration?.ieExportDeclaration ||
            declaration?.ieArrivalAtExitDeclaration ||
            declaration?.ieNctsDeclaration
        ) {
            return customer?.hasAesCertificate;
        } else if (
            declaration?.irelandImportDeclaration ||
            declaration?.irelandH7ImportDeclaration ||
            declaration?.ieImportTemporaryStorageDeclaration ||
            declaration?.ieImportElectronicTransportDocument
        ) {
            return customer?.hasAisCertificate;
        } else if (declaration?.ics2ENSDeclaration) {
            return true; //TODO: For now we hardcode this. The BE will expose a property in the future that we can use.
        } else if (declaration?.preBoardingNotification) {
            return true;
        }
    }, [customer, individual, declaration]);

    const showSubmissionErrorsDrawer = shouldShowSubmissionErrors(declaration, error);

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'absolute', right: 0, bottom: 21 }}>
                <InvoiceModalButton declaration={declaration} saveAsDraft={saveAsDraft} formikValues={formik.values} />

                {showSubmissionErrorsDrawer && <SubmissionErrorsDrawerButton declarationError={error} />}
                <DeclarationNotificationsDrawerButton declaration={declaration} formik={formik} />
            </div>

            <SubmissionModalsManager
                customerId={declaration?.customerId}
                isGvms={Boolean(declaration?.gvmsDeclaration)}
                hasRightCertificate={hasRightCertificate}
                submitting={submitting}
                submissionModalsActions={submissionModalsActions}
            />

            <AlertManager declaration={declaration} hasRightCertificate={hasRightCertificate} />
        </>
    );
};

export default DeclarationPopupManager;
